import { ArrowRight, Compare } from '@carbon/react/icons';
import { ActionableNotification, Tag } from '@carbon/react';
import { LaunchLink } from 'components/launch-link';
import { parseLocalDateTime } from 'utils/dates';
import { formatNumber } from 'utils/number';
import type {
  CompareRunEvalSide,
  PackageCompareRole,
  PackageCompareRunRow,
} from 'types/packages-api/responses';
import {
  buildSavedCompareResultUrl,
  evalVersionToSource,
  formatSource,
} from '../../compare/wizard/utils';
import { usePackageCompareRuns } from './hooks';
import { useEval3Page } from './context';
import styles from './package-compare-runs-panel.module.scss';

/**
 * The package's side in the compare, phrased for the chip, with its Carbon Tag colour.
 * Deliberately spells out the Target case: the whole point of the panel is that it surfaces
 * compares the package only took part in as the Target, not only the ones it originated.
 */
const ROLE_TAGS: Record<
  PackageCompareRole,
  { label: string; type: 'blue' | 'teal' | 'purple' }
> = {
  origin: { label: 'As Origin', type: 'blue' },
  target: { label: 'As Target', type: 'teal' },
  both: { label: 'Both sides', type: 'purple' },
};

/**
 * An eval's one-line name: the user's description, or a version-qualified id when it has none.
 *
 * @param {CompareRunEvalSide} side - The compare side to label.
 * @return {string} The eval's display name.
 */
function sideLabel(side: CompareRunEvalSide): string {
  if (side.description) return side.description;
  // Compose the two existing exports rather than re-encoding the version map here — an unexpected
  // version then reads honestly (e.g. "eval_9") instead of being mislabelled "3.0".
  const version = formatSource(evalVersionToSource(side.evalVersion));
  return `Evaluation ${version} #${side.evaluationId}`;
}

/**
 * The variance threshold the compare ran with, as a trimmed percentage (0.05 -> "5%",
 * 0.025 -> "2.5%") — part of a compare's identity, so two runs of the same pair at
 * different thresholds read apart.
 *
 * @param {number} threshold - The fractional variance threshold.
 * @return {string} The percentage label.
 */
function thresholdLabel(threshold: number): string {
  // formatNumber (min 0, max 2 fraction digits) gives "5" and "2.5" and clears the binary-float
  // dust (0.025*100 -> 2.5, not 2.4999…) through the one display-formatting helper the lint rule
  // reserves this for — no hand-rolled toFixed.
  return `±${formatNumber(threshold * 100, 0, 2)}%`;
}

/**
 * One side of a compare: its eval name, its workspace, and — only when the eval lives in a
 * DIFFERENT package than the one being viewed — that package's name, so a cross-package
 * comparison reads as one rather than looking like a same-package run.
 *
 * @param {object} props - The run side's props.
 * @param {CompareRunEvalSide} props.side - The eval on this side of the compare.
 * @param {number} props.currentPackageId - The package currently being viewed.
 * @return {JSX.Element} The side's labelled cell.
 */
function RunSide({
  side,
  currentPackageId,
}: {
  side: CompareRunEvalSide;
  currentPackageId: number;
}): JSX.Element {
  const foreign =
    side.packageId !== null && side.packageId !== currentPackageId;
  const secondary = [side.workspaceName, foreign ? side.packageName : null]
    .filter(Boolean)
    .join(' · ');
  return (
    <span className={styles.side}>
      <span className={styles.sideName}>{sideLabel(side)}</span>
      {secondary && <span className={styles.sideMeta}>{secondary}</span>}
    </span>
  );
}

/**
 * Lists the package's saved Package Compares on the eval page, most-recently-run first — every
 * comparison the package takes part in, whether it is the Origin or the Target (INS-12510).
 * Each row reopens on the comparison result page, which re-runs the compare LIVE from the saved
 * inputs (both side evals + the variance threshold) — never a frozen snapshot.
 *
 * Renders nothing when the package has never been compared (so it looks exactly as before) and
 * nothing while the first load is in flight (no flash); a failed load shows a compact, retryable
 * Carbon notification rather than vanishing silently.
 *
 * @return {JSX.Element | null} The runs panel, or null when there is nothing to show.
 */
export function PackageCompareRunsPanel(): JSX.Element | null {
  const { packageId } = useEval3Page();
  const { data, isLoading, isError, refetch } =
    usePackageCompareRuns(packageId);
  const runs = data?.runs ?? [];

  // Nothing to announce yet: stay invisible rather than flash an empty shell.
  if (isLoading) return null;

  // Only show the error state when there is no list to show. refetchOnWindowFocus is on and retry is
  // 0 app-wide, so a transient 5xx on a focus refetch sets isError while `data` still holds the last
  // good list — keep rendering it rather than replacing it with the banner (INS-12510 review).
  if (isError && !data) {
    return (
      <section className={styles.panel} aria-label="Package Compare runs">
        <PanelHeading />
        <ActionableNotification
          kind="error"
          lowContrast
          hideCloseButton
          inline
          title="Couldn’t load this package’s comparisons."
          actionButtonLabel="Try again"
          onActionButtonClick={() => refetch()}
        />
      </section>
    );
  }

  // A package that was never compared reads exactly as it did before this panel existed.
  if (runs.length === 0) return null;

  return (
    <section className={styles.panel} aria-label="Package Compare runs">
      <PanelHeading />
      <ul className={styles.list}>
        {runs.map((run) => (
          <RunRow key={run.id} run={run} packageId={packageId} />
        ))}
      </ul>
    </section>
  );
}

/**
 * One saved compare: the Origin → Target pair, this package's role, the variance threshold and
 * when/by whom it last ran, and an Open link that re-runs it live in a new tab.
 *
 * @param {object} props - The run row's props.
 * @param {PackageCompareRunRow} props.run - The saved compare.
 * @param {number} props.packageId - The package currently being viewed.
 * @return {JSX.Element} The row.
 */
function RunRow({
  run,
  packageId,
}: {
  run: PackageCompareRunRow;
  packageId: number;
}): JSX.Element {
  const role = ROLE_TAGS[run.packageRole];
  return (
    <li className={styles.row}>
      <div className={styles.pair}>
        <RunSide side={run.origin} currentPackageId={packageId} />
        <ArrowRight size={16} className={styles.arrow} aria-label="to" />
        <RunSide side={run.target} currentPackageId={packageId} />
      </div>
      <div className={styles.meta}>
        <Tag size="sm" type={role.type}>
          {role.label}
        </Tag>
        <span className={styles.when}>
          {thresholdLabel(run.varianceThreshold)}
        </span>
        <span className={styles.when}>
          {parseLocalDateTime(run.lastRunAt, true)}
          {run.lastRunByName ? ` · ${run.lastRunByName}` : ''}
        </span>
      </div>
      <LaunchLink
        to={buildSavedCompareResultUrl(
          run.origin,
          run.target,
          run.varianceThreshold
        )}
        className={styles.open}
        label={`Reopen the comparison of ${sideLabel(
          run.origin
        )} and ${sideLabel(run.target)} at ${thresholdLabel(
          run.varianceThreshold
        )} in a new tab`}
      >
        Open
      </LaunchLink>
    </li>
  );
}

/**
 * The panel's icon + title + one-line subtitle.
 *
 * @return {JSX.Element} The heading row.
 */
function PanelHeading(): JSX.Element {
  return (
    <div className={styles.heading}>
      <Compare size={20} className={styles.icon} />
      <div className={styles.headingText}>
        <span className={styles.title}>Package Compare runs</span>
        <span className={styles.subtitle}>
          Comparisons run for this package — reopen one to re-run it live.
        </span>
      </div>
    </div>
  );
}
